import { fetcher } from '../../utility/domUtils';
import templateData from '../../utility/getTemplateData';
import * as AppActions from '../../actions/AppActions';
import { showSpinnerOverlay } from '../../utility/helper';
import { trackEventWithData } from '../../fpti/analytics';
import { trackErrorIM } from '../../fpti/fpti';
import { FPTI_TAGS } from '../../fpti/fptiConstants';
import { EVENT_NAME } from '../../utility/constants';

/**
 * Executes the capture order API call for Venmo payments.
 * Sends a POST request to capture the authorized payment and returns the response data.
 *
 * @param {string} orderToken - The order token to capture
 * @returns {Promise<Object>} The capture response data
 * @throws {Error} If no capture data is received from the API
 */
const executeCaptureOrder = async (orderToken) => {
  const response = await fetcher(
    `/donate/venmo/orders/${orderToken}/capture?dtoken=${templateData.token}`,
    {
      method: 'POST',
    }
  );

  const result = await response.json();
  if (result?.data) {
    return result.data;
  }
  throw new Error('No capture data received from API');
};

/**
 * Handles navigation to the confirmation page after successful payment.
 * Constructs the appropriate URL based on whether a token is present.
 *
 * @param {Object} history - React Router history object for navigation
 */
const handleConfirmation = (history) => {
  const url = templateData.token
    ? `/donate/guest/confirmation/?token=${templateData.token}`
    : '/donate/guest/confirmation';

  history.push(url);
};

/**
 * Handles successful payment processing by updating app state and navigating to confirmation.
 *
 * @param {Object} response - The payment response data
 * @param {Object} history - React Router history object for navigation
 */
const handlePaymentSuccess = (response, history) => {
  AppActions.updateConfirmation({ confirmation: response });
  handleConfirmation(history);
};

/**
 * Main function to capture a Venmo payment order.
 * This function handles the complete payment capture flow including:
 * - Showing loading spinner
 * - Executing the capture API call
 * - Validating the response and confirmation data
 * - Updating app state with order status and confirmation
 * - Tracking analytics events for success/failure
 * - Navigating to appropriate pages (confirmation or error)
 *
 * @param {Object} data - Payment data containing orderID
 * @param {Object} _actions - PayPal actions object (unused)
 * @param {Object} history - React Router history object for navigation
 * @returns {Promise<Object>} The capture result data
 * @throws {Error} If capture fails or validation errors occur
 */
export const captureOrder = async (data, _actions, history) => {
  showSpinnerOverlay();

  try {
    const result = await executeCaptureOrder(data.orderID);
    if (!result) {
      throw new Error('No result received from capture order');
    }

    const { confirmationData = {} } = result;
    const confirmationNumber =
      confirmationData?.confirmationNumber ||
      confirmationData?.postBackData?.txn_id;

    if (!confirmationNumber) {
      throw new Error('No confirmation number received from capture order');
    }

    AppActions.updateOrderStatus({ orderStatus: 'COMPLETED' });
    AppActions.updateConfirmation({ confirmation: confirmationData });
    trackEventWithData({
      event_name: EVENT_NAME.DONATE_VENMO_CAPTURE_ORDER_SUCCEEDED,
      data: {
        [FPTI_TAGS.ORDER_ID]: data?.orderID,
        [FPTI_TAGS.TXN_ID]: confirmationNumber,
      },
    });
    handlePaymentSuccess(confirmationData, history);

    return result;
  } catch (error) {
    trackErrorIM({
      errorCode: error?.status || 'donate_venmo_capture_order_failed',
      errorMessage: error?.message || 'Venmo order payment failed.',
      fieldName: 'Venmo Payment',
      additionalData: {
        [FPTI_TAGS.EVENT_NAME]: EVENT_NAME.DONATE_VENMO_CAPTURE_ORDER_FAILED,
        [FPTI_TAGS.ORDER_ID]: data?.orderID,
      },
    });
    history.push(`/donate/error/payment?token=${templateData.token}`);
    throw error;
  }
};

export default captureOrder;
